import React, { useEffect, useState, useRef } from 'react';
import RichTextEditor from 'react-rte';
import { convertToHTML } from 'src/utils/commonUtils';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Button, CircularProgress } from '@mui/material';
import { Code, TextFields, Image as ImageIcon } from '@mui/icons-material';
import apiInstance from 'src/utils/apiAuth';

/**
 * Checks if a string is a full HTML document (email template)
 * @param {string} str - The string to check
 * @returns {boolean} - True if it's a full HTML document
 */
const isFullHtmlDocument = (str) => {
    if (!str) return false;
    const trimmed = str.trim().toLowerCase();
    // Check for DOCTYPE or html tag at the start
    return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
};

const TextEditor = (props) => {
    const { allowHtmlMode = false, allowImageUpload = false } = props;
    const imageInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    // Determine initial mode based on content
    const getInitialMode = () => {
        if (allowHtmlMode && props.value && isFullHtmlDocument(props.value)) {
            return 'html';
        }
        return 'rich';
    };
    
    const [editorMode, setEditorMode] = useState(getInitialMode);
    const [rawHtmlValue, setRawHtmlValue] = useState(props.value || '');
    
    const initialContent = props?.value ? convertToHTML(props?.value) : "";
    const [editorState, setEditorState] = useState(initialContent ? RichTextEditor.createValueFromString(initialContent, 'html') : RichTextEditor.createEmptyValue());

    useEffect(() => {
        // Update raw HTML value when props change
        if (props.value !== undefined) {
            setRawHtmlValue(props.value || '');
        }
        
        // Auto-detect mode based on content
        if (allowHtmlMode && props.value && isFullHtmlDocument(props.value)) {
            setEditorMode('html');
        }
    }, [props.value, allowHtmlMode]);

    useEffect(() => {
        if (editorMode === 'rich') {
            const newContent = props.value ? convertToHTML(props.value) : "";
            const newEditorState = newContent ? RichTextEditor.createValueFromString(newContent, 'html') : RichTextEditor.createEmptyValue();
            if (editorState.toString('html') !== newEditorState.toString('html')) {
                setEditorState(newEditorState);
            }
        }
    }, [props.value, editorMode]);

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setEditorMode(newMode);
            
            // When switching to rich mode, try to load the HTML content
            if (newMode === 'rich' && rawHtmlValue) {
                try {
                    const newEditorState = RichTextEditor.createValueFromString(rawHtmlValue, 'html');
                    setEditorState(newEditorState);
                } catch (e) {
                    // If HTML can't be parsed, create empty state
                    setEditorState(RichTextEditor.createEmptyValue());
                }
            }
            // When switching to HTML mode, get current rich text as HTML
            if (newMode === 'html') {
                const html = editorState.toString('html');
                const isEmptyContent = /^<p><br><\/p>$/.test(html);
                setRawHtmlValue(isEmptyContent ? '' : html);
            }
        }
    };

    const handleChange = (value) => {
        setEditorState(value);
        const html = value.toString('html');
        const isEmptyContent = /^<p><br><\/p>$/.test(html);
        const finalHtml = isEmptyContent ? "" : html;
        props?.onChange(finalHtml);
    };

    const handleRawHtmlChange = (e) => {
        const value = e.target.value;
        setRawHtmlValue(value);
        props?.onChange(value);
    };
    
    const handleFontChange = (event) => {
        const font = event?.target?.value;
        props?.onFontChange?.(font);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await apiInstance.post('campaigns/upload-image/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const imageUrl = response?.data?.data?.url;
            if (imageUrl) {
                const imgTag = `<img src="${imageUrl}" alt="${file.name}" style="max-width:100%;" />`;
                if (editorMode === 'html') {
                    const newHtml = rawHtmlValue + imgTag;
                    setRawHtmlValue(newHtml);
                    props?.onChange(newHtml);
                } else {
                    // Insert into rich text editor by converting current content + image
                    const currentHtml = editorState.toString('html');
                    const isEmptyContent = /^<p><br><\/p>$/.test(currentHtml);
                    const newHtml = (isEmptyContent ? '' : currentHtml) + imgTag;
                    const newEditorState = RichTextEditor.createValueFromString(newHtml, 'html');
                    setEditorState(newEditorState);
                    props?.onChange(newHtml);
                }
            }
        } catch (err) {
            console.error('Image upload failed:', err);
        } finally {
            setIsUploading(false);
            // Reset file input so same file can be uploaded again
            if (imageInputRef.current) imageInputRef.current.value = '';
        }
    };

    const toolbarConfig = {
        display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'HISTORY_BUTTONS'],
        INLINE_STYLE_BUTTONS: [
            { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
            { label: 'Italic', style: 'ITALIC' },
            { label: 'Underline', style: 'UNDERLINE' }
        ],
        BLOCK_TYPE_BUTTONS: [
            { label: 'UL', style: 'unordered-list-item' },
            { label: 'OL', style: 'ordered-list-item' }
        ]
    };

    const customControl = [
        () => (
            <select onChange={handleFontChange} value={props.selectedFont} style={{height: '30px'}}>
                <option value="Calibri">Calibri</option>
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
            </select>
        ),
    ];

    const customStyleMap = {
        FONT_ARIAL: { fontFamily: 'Arial, sans-serif' },
        FONT_TIMES_NEW_ROMAN: { fontFamily: 'Times New Roman, serif' },
        FONT_CALIBRI: { fontFamily: 'Calibri, sans-serif' }
    };

    return (
        <div style={{ minWidth: "50%", ...props.style }}>
            {/* Toolbar row: Image upload + Mode Toggle */}
            {(allowHtmlMode || allowImageUpload) && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Image Upload */}
                    {allowImageUpload && (
                        <Box>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                ref={imageInputRef}
                                style={{ display: 'none' }}
                                onChange={handleImageUpload}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={isUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                                onClick={() => imageInputRef.current?.click()}
                                disabled={isUploading || props?.disabled}
                                sx={{ textTransform: 'none' }}
                            >
                                {isUploading ? 'Uploading...' : 'Insert Image'}
                            </Button>
                        </Box>
                    )}
                    {!allowImageUpload && <Box />}

                    {/* Mode Toggle */}
                    {allowHtmlMode && (
                        <ToggleButtonGroup
                            value={editorMode}
                            exclusive
                            onChange={handleModeChange}
                            size="small"
                            aria-label="editor mode"
                        >
                            <ToggleButton value="rich" aria-label="rich text mode">
                                <Tooltip title="Rich Text Editor">
                                    <TextFields fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="html" aria-label="html mode">
                                <Tooltip title="HTML Code Editor (for email templates)">
                                    <Code fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            )}

            {/* Rich Text Editor Mode */}
            {editorMode === 'rich' && (
                <RichTextEditor
                    customControls={props?.onFontChange ? customControl : null}
                    placeholder={props.placeholder}
                    value={editorState}
                    customStyleMap={customStyleMap}
                    onChange={handleChange}
                    className="text-editor"
                    toolbarConfig={toolbarConfig}
                    rootStyle={{
                        fontFamily: props.selectedFont ? props.selectedFont : 'inherit',
                        minHeight: props.minHeight,
                        borderRadius: "8px",
                        ...props.editorStyle,
                    }}
                    editorStyle={{
                        fontSize: props?.matches ? "0.675rem" : '16px',
                        maxHeight: "50vh",
                        overflow: "auto",
                    }}
                    disabled={props?.disabled}
                />
            )}

            {/* Raw HTML Editor Mode */}
            {editorMode === 'html' && (
                <textarea
                    value={rawHtmlValue}
                    onChange={handleRawHtmlChange}
                    placeholder="Paste your HTML email template here..."
                    disabled={props?.disabled}
                    style={{
                        width: '100%',
                        minHeight: props.minHeight || '200px',
                        maxHeight: '50vh',
                        padding: '12px',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        resize: 'vertical',
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                />
            )}
        </div>
    );
}

TextEditor.defaultProps = {
    name: "",
    value: "",
    placeholder: "Add your comment here ...",
    style: {},
    editorStyle: {},
    minHeight: "100%",
    isRequired: false,
    disabled: false,
    selectedFont: '',
    allowHtmlMode: false, // Set to true to enable HTML code editor toggle for email templates
    allowImageUpload: false, // Set to true to enable image upload button for newsletters
}

export default TextEditor;
